import React, { useEffect, useState } from 'react';
import { Button, Input, Space, Select, Checkbox } from 'antd';
import classNames from 'classnames';
import { useLocation, useNavigate } from 'react-router-dom';
import { getQueryString } from 'utils/utils';
import { RefCode, BizCategory, PmCode, CompanyCode} from 'components/TableSearchBox';
import { addNewSearch } from 'utils/utils';
import 'components/TableSearchBox/index.scss';
import { SearchRangePicker } from 'components/atoms'
import moment, { Moment } from 'moment';
import { RangeValue } from 'rc-picker/lib/interface';
import { dateFormat } from 'utils/date-utils';

const cx = classNames.bind('./index.scss');
export interface ProjectSearchBoxProp {}

const ProjectSearchBox = ({}: ProjectSearchBoxProp): React.ReactElement => {
  /* ======================================================
   * hooks
   * ====================================================== */
  const navigate = useNavigate();
  const location = useLocation();

  /* ======================================================
   * variables
   * ====================================================== */

  /* ======================================================
   * states
   * ====================================================== */
  const [searchInput, setSearchInput] = useState<string>('');
  const [searchRange, setSearchRange] = useState<RangeValue<Moment>|null>();
  const [biz, setBiz] = useState<number>();
  const [company, setCompany] = useState<number>();
  const [searchTarget, setSearchTarget] = useState<string|undefined>(undefined);
  const [searchStep, setSearchStep] = useState<string|undefined>(undefined);
  const [searchSubject, setSearchSubject] = useState<string|undefined>(undefined);
  const [searchType, setSearchType] = useState<string|undefined>(undefined);
  const [searchPm, setSearchPm] = useState<string|undefined>(undefined);
  const [searchViewOnly, setSearchViewOnly] = useState<string|undefined>(undefined);


  /* ======================================================
   * events
   * ====================================================== */
  const onChange = (e) => {
    setSearchInput(e.target.value);
  };

  const onClickReset = (e) => {
    setSearchInput('');
    setSearchRange(null);
    setBiz(0);
    setCompany(0);
    setSearchTarget(undefined);
    setSearchStep(undefined);
    setSearchSubject(undefined);
    setSearchType(undefined);
    setSearchPm(undefined);

    navigate(location.pathname);
  };

  const onSearch = (data: any) => {
    navigate({
      ...location,
      search: addNewSearch(location.search, data),
    });    
  };

  const onChangePeriod = (date: RangeValue<Moment>, dateString: [string, string]) => {
    setSearchRange(date);

    const searchDateS = dateString[0] ? dateFormat(dateString[0], 'YYYYMMDD') : '';
    const searchDateE = dateString[1] ? dateFormat(dateString[1], 'YYYYMMDD') : '';

    navigate({
      ...location,
      search: addNewSearch(location.search, { searchDateStart: searchDateS, searchDateEnd: searchDateE, pageNo: 1 }),
    });   
  };

  /* ======================================================
   * effects
   * ====================================================== */

  useEffect(() => {
    const historySearch = getQueryString(location.search);
    (historySearch.search)?setSearchInput(historySearch.search):setSearchInput('');
    if (historySearch.searchDateStart && historySearch.searchDateEnd) {
      setSearchRange([moment(historySearch.searchDateStart, "YYYY-MM-DD"), moment(historySearch.searchDateEnd, "YYYY-MM-DD")]);
    } else {
      setSearchRange(null);
    }  
    if (historySearch.biz) setBiz(historySearch.biz);
    if (historySearch.company) setCompany(historySearch.company);
    if (historySearch.target) setSearchTarget(historySearch.target);
    if (historySearch.step) setSearchStep(historySearch.step);
    if (historySearch.lecture_type) setSearchType(historySearch.lecture_type);
    if (historySearch.lecture_subject) setSearchSubject(historySearch.lecture_subject);
    if (historySearch.pm) setSearchPm(historySearch.pm);
    setSearchViewOnly(historySearch.viewOnly);

  }, [location.search]);

  /* ======================================================
   * render items
   * ====================================================== */
  return (
    <div className={cx('tableSearchBoxWrapper')}>
      <Space direction='vertical'>
        <Space>
        <SearchRangePicker value={searchRange} format="YYYY-MM-DD" onChange={onChangePeriod} style={{width:'250px'}} /> 
          <BizCategory defaultValue={biz} onChangeSelect={(value)=>onSearch({ biz: value, pageNo: 1 })} />          
          <RefCode codeValue={searchTarget} fieldName={'lecture_target'} placeholder={'교육대상'} onChangeSelect={(value)=>onSearch({ target: value, pageNo: 1 })} />
          <Select placeholder="진행상태" onChange={(value)=>onSearch({ step: value, pageNo: 1 })} value={searchStep}>
              <Select.Option key={'A'} value={'A'}>전체</Select.Option>               
              <Select.Option key={'B'} value={'B'}>일정논의</Select.Option>
              <Select.Option key={'C'} value={'C'}>진행협의</Select.Option>
          </Select> 
        </Space>
        <Space>
          <PmCode codeValue={(searchPm && searchPm !== 'A')?Number(searchPm):undefined} onChangeSelect={(value)=>onSearch({ pm: value, pageNo: 1 })} />                
          <RefCode codeValue={searchSubject} fieldName={'lecture_subject1'} placeholder={'강의주제'} onChangeSelect={(value)=>onSearch({ lecture_subject: value, pageNo: 1 })} />
          <RefCode codeValue={searchType} fieldName={'lecture_type1'} placeholder={'강의구분'} onChangeSelect={(value)=>onSearch({ lecture_type: value, pageNo: 1 })} />
          <CompanyCode codeValue={company} onChangeSelect={(value)=>onSearch({ company: value, pageNo: 1 })} />
          <Input.Search
            className={cx('searchInput')}
            value={searchInput}
            placeholder="프로젝트명 또는 기업명을 입력하세요"
            onChange={onChange}
            onSearch={(value)=>onSearch({ search: value, pageNo: 1 })}
            allowClear
          />
          <Checkbox checked={searchViewOnly === 'Y'}  onChange={(e)=>onSearch({ viewOnly: e.target.checked?'Y':'', pageNo: 1 })}>검색내용만 보기</Checkbox> 
          <Button onClick={onClickReset}>
            검색초기화
          </Button>                
        </Space>      
      </Space>
    </div>
  );
};

export default ProjectSearchBox;
